import { useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { useFilterToolbarState } from 'hooks/filterToolbar/useFilterToolbarState';
import { useAreGroupFiltersReady } from 'hooks/projectMap/useAreGroupFiltersReady';
import { useScoutbotSavedSearchQueries } from 'hooks/projectMap/useScoutbotSavedSearchQueries';
import { useDebounce } from 'hooks/useDebounced';
import { usePaginate } from 'hooks/usePaginate';
import { fetchAddressList } from 'pages/ProjectInsights/services';
import {
  useAddressSearch,
  useSelectedPolygonBoundsOnMap,
  useShowAllProjectsWithinPolygon,
} from 'state/map';
import {
  useMapBoundsAsCardinalDirections,
  useMapBoundsAsString,
} from 'state/mapSettings';
import { Group } from 'types';

type Props = {
  useMapFiltersOnly?: boolean;
  allowAddressSearch?: boolean;
  overrideBounds?: Group['polygon_bounds'];
};

export const useMapProjectList = ({
  overrideBounds,
  useMapFiltersOnly,
  allowAddressSearch,
}: Props = {}) => {
  const { page: pageQuery, setPageNumber: setPage } = usePaginate();
  const [mapBoundsAsCardinalDirections] = useMapBoundsAsCardinalDirections();
  const [mapBoundsAsString] = useMapBoundsAsString();
  const previousFilters = useRef({});
  const [selectedPolygonBoundsOnMap] = useSelectedPolygonBoundsOnMap();
  const debouncedMapBounds = useDebounce(mapBoundsAsCardinalDirections, 800);
  const [showAllProjectsWithinPolygon] = useShowAllProjectsWithinPolygon();
  const { filterFieldsState, getFiltersAsQueryParams, getMapStateAsQueryParams } =
    useFilterToolbarState();
  const [addressSearch] = useAddressSearch();
  const debouncedAddressSearch = useDebounce(addressSearch, 500);
  const { isScoutSearch, rawQueries } = useScoutbotSavedSearchQueries();
  const areGroupFiltersReady = useAreGroupFiltersReady();

  const projectList = useQuery(
    [
      'map-search-project-list',
      filterFieldsState,
      pageQuery,
      debouncedMapBounds,
      selectedPolygonBoundsOnMap,
      useMapFiltersOnly,
      overrideBounds,
      showAllProjectsWithinPolygon,
      debouncedAddressSearch,
      isScoutSearch,
      rawQueries,
    ],
    ({ pageParam = pageQuery }) => {
      if (
        !useMapFiltersOnly &&
        showAllProjectsWithinPolygon &&
        selectedPolygonBoundsOnMap
      ) {
        return fetchAddressList({
          pathOrBounds:
            overrideBounds || selectedPolygonBoundsOnMap || debouncedMapBounds,
        });
      }

      const allFilters = getFiltersAsQueryParams();
      const mapState = getMapStateAsQueryParams();
      // A scoutbot search's real filters live in raw_queries - the
      // individual filter fields stay empty, so only the date/status fields
      // (which the raw-queries path also applies) still apply on top of it.
      const filterState = isScoutSearch
        ? {
            raw_queries: rawQueries,
            startDate: allFilters.startDate,
            createdAtStart: allFilters.createdAtStart,
            createdAtEnd: allFilters.createdAtEnd,
            status: allFilters.status,
          }
        : allFilters;

      if (useMapFiltersOnly) {
        return fetchAddressList({
          ...filterState,
          pathOrBounds: overrideBounds || undefined,
          page: pageParam,
          includeDocumentsInSearch: true,
          ...(allowAddressSearch && {
            address: debouncedAddressSearch,
          }),
        });
      }

      return fetchAddressList({
        ...filterState,
        ...mapState,
        pathOrBounds: selectedPolygonBoundsOnMap || debouncedMapBounds,
        page: pageParam,
      });
    },
    {
      getNextPageParam: (lastPage) => lastPage?.nextPage,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      // Hold off while a saved-search switch is in flight so we don't fetch
      // with the previous search's filters (with no group selected this is
      // always ready). keepPreviousData keeps the current list visible until
      // the correct one lands.
      enabled: areGroupFiltersReady,
    },
  );

  // Reset page and projects if any filter other than page has changed
  useEffect(() => {
    if (
      Object.keys(previousFilters.current).length > 0 && // check if the object has any keys
      Object.keys(filterFieldsState).length > 0 && // check if the object has any keys
      JSON.stringify(previousFilters.current) !== JSON.stringify(filterFieldsState)
    ) {
      setPage(1);
    }

    previousFilters.current = filterFieldsState;
  }, [filterFieldsState]);

  useEffect(() => {
    setPage(1);
  }, [mapBoundsAsString]);

  return {
    projectList,
  };
};
